import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useParams, Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useEffect, useState } from "react";
import { Plus, Check, ExternalLink, Loader2 } from "lucide-react";
import api from "@/lib/api";
import { useLoc } from "@/lib/loc";
import { useAuth } from "@/context/AuthContext";
import HlsPlayer from "@/components/HlsPlayer";
import VideoCard from "@/components/VideoCard";
import ShareButtons from "@/components/ShareButtons";
import Seo from "@/components/Seo";
import { toast } from "sonner";

export default function VideoDetail() {
  const { slug } = useParams();
  const { t } = useTranslation();
  const loc = useLoc();
  const { user } = useAuth();
  const qc = useQueryClient();
  const [inList, setInList] = useState(false);

  const { data, isLoading } = useQuery({
    queryKey: ["video", slug],
    queryFn: async () => (await api.get(`/videos/${slug}`)).data,
  });

  const { data: myList } = useQuery({
    queryKey: ["mylist"],
    queryFn: async () => (await api.get("/account/my-list")).data,
    enabled: !!user,
  });

  useEffect(() => {
    if (myList && data?.video) setInList(myList.some((v) => v.id === data.video.id));
  }, [myList, data]);

  useEffect(() => window.scrollTo(0, 0), [slug]);

  if (isLoading)
    return (
      <div className="flex h-screen items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  if (!data) return null;

  const { video, related, creator, category } = data;

  const toggleList = async () => {
    if (!user) {
      toast.info("Connectez-vous pour utiliser Ma liste.");
      return;
    }
    try {
      if (inList) {
        await api.delete(`/account/my-list/${video.id}`);
        setInList(false);
      } else {
        await api.post(`/account/my-list/${video.id}`);
        setInList(true);
      }
      qc.invalidateQueries({ queryKey: ["mylist"] });
    } catch (_) {}
  };

  return (
    <div className="pt-16">
      <Seo
        title={loc(video.title)}
        description={loc(video.description)}
        image={video.thumbnail_url}
        video={{ duration: video.duration ? `PT${Math.round(video.duration)}S` : undefined, uploadDate: video.created_at }}
      />

      <div className="mx-auto max-w-[1400px] px-4 py-6 md:px-12">
        <HlsPlayer videoId={video.id} poster={video.thumbnail_url} />

        <div className="mt-6 grid grid-cols-1 gap-8 lg:grid-cols-3">
          <div className="lg:col-span-2">
            <div className="flex flex-wrap items-center gap-2">
              {category && (
                <Link to={`/browse?category=${category.id}`} className="rounded-full bg-white/5 px-3 py-1 text-xs text-muted-foreground transition-colors hover:text-foreground">
                  {loc(category.name)}
                </Link>
              )}
              {video.is_sponsored && (
                <span className="rounded-sm border border-white/10 bg-black/60 px-2 py-1 text-[10px] uppercase tracking-widest text-white/80 backdrop-blur-md">
                  {t("common.sponsored")}
                </span>
              )}
            </div>

            <h1 className="mt-3 font-heading text-3xl font-bold tracking-tight text-foreground md:text-4xl">
              {loc(video.title)}
            </h1>
            <p className="mt-1 text-sm text-muted-foreground">
              {(video.views || 0).toLocaleString("fr-FR")} {t("common.views")}
            </p>

            <div className="mt-4 flex gap-3">
              <button
                onClick={toggleList}
                data-testid="mylist-toggle"
                className="flex items-center gap-2 rounded-full border border-white/15 bg-white/5 px-5 py-2.5 text-sm font-semibold transition-colors hover:bg-white/10"
              >
                {inList ? <Check className="h-4 w-4 text-primary" /> : <Plus className="h-4 w-4" />}
                {inList ? t("common.inList") : t("common.addToList")}
              </button>
            </div>

            <div className="mt-5">
              <ShareButtons slug={video.slug || video.id} title={loc(video.title)} />
            </div>

            {video.is_sponsored && (
              <div className="mt-5 rounded-lg border border-primary/20 bg-primary/5 px-4 py-3 text-sm text-primary/90" data-testid="sponsor-disclosure">
                {t("detail.sponsorDisclosure", { sponsor: video.sponsor_name || "un partenaire" })}
              </div>
            )}

            <p className="mt-5 whitespace-pre-line leading-relaxed text-white/70">
              {loc(video.description)}
            </p>

            {video.ecosystem?.external_cta_url && (
              <a
                href={video.ecosystem.external_cta_url}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="ecosystem-cta"
                className="mt-5 inline-flex items-center gap-2 rounded-full bg-primary px-5 py-2.5 text-sm font-semibold text-primary-foreground transition-colors hover:bg-[hsl(var(--primary))]/90"
              >
                {t("common.learnMore")} <ExternalLink className="h-4 w-4" />
              </a>
            )}
          </div>

          <aside className="lg:col-span-1">
            {creator && (
              <Link
                to={`/creators/${creator.slug || creator.id}`}
                data-testid="creator-card"
                className="block rounded-xl border border-white/5 bg-[hsl(var(--surface-elevated))] p-4 transition-colors hover:border-white/15"
              >
                <p className="mb-3 text-xs uppercase tracking-widest text-muted-foreground">{t("detail.aboutCreator")}</p>
                <div className="flex items-center gap-3">
                  <img src={creator.avatar_url} alt={creator.name} className="h-12 w-12 rounded-full object-cover ring-1 ring-white/10" />
                  <div>
                    <p className="font-heading font-semibold text-foreground">{creator.name}</p>
                    <p className="text-xs text-muted-foreground">{loc(creator.title)}</p>
                  </div>
                </div>
              </Link>
            )}
          </aside>
        </div>

        {related?.length > 0 && (
          <section className="mt-12">
            <h2 className="mb-4 font-heading text-xl font-semibold tracking-tight">{t("common.relatedTitle")}</h2>
            <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5">
              {related.map((v) => (
                <div key={v.id} className="w-full">
                  <VideoCard video={v} className="w-full" />
                </div>
              ))}
            </div>
          </section>
        )}
      </div>
    </div>
  );
}
